import { Shield } from "lucide-react";
import { Badge } from "../ui/badge";

export default function FraudStatsSection({ fraudStats, onFilterChange, activeFilter }) {
  if (!fraudStats) return null;

  const handleClick = (filter) => {
    if (onFilterChange) {
      // Toggle off if clicking the same filter
      onFilterChange(activeFilter === filter ? "all" : filter);
    }
  };

  const isActive = (filter) => activeFilter === filter;

  return (
    <div className="p-4 border-b border-slate-100 bg-slate-50/50" data-testid="fraud-stats-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Shield className="w-4 h-4" />
          Fraud Detection Overview
          <span className="text-xs font-normal text-slate-500">(Click to filter)</span>
        </h3>
        <Badge variant="outline" className="text-xs">
          {fraudStats.checked_users} / {fraudStats.total_users} users checked
        </Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-white rounded-lg p-3 border border-slate-200">
          <p className="text-xs text-slate-500 mb-1">Avg Score</p>
          <p className={`text-lg font-bold ${fraudStats.average_fraud_score >= 50 ? 'text-red-600' : fraudStats.average_fraud_score >= 25 ? 'text-amber-600' : 'text-green-600'}`}>
            {fraudStats.average_fraud_score}
          </p>
        </div>
        <div 
          onClick={() => handleClick("flagged")}
          className={`bg-white rounded-lg p-3 border cursor-pointer transition-all hover:scale-105 ${isActive("flagged") ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-red-200 hover:border-red-400'}`}
          data-testid="fraud-filter-flagged"
        >
          <p className="text-xs text-slate-500 mb-1">Flagged</p>
          <p className="text-lg font-bold text-red-600">{fraudStats.flagged_users || 0}</p>
        </div>
        <div 
          onClick={() => handleClick("vpn")}
          className={`bg-white rounded-lg p-3 border cursor-pointer transition-all hover:scale-105 ${isActive("vpn") ? 'border-amber-500 ring-2 ring-amber-200 bg-amber-50' : 'border-slate-200 hover:border-amber-400'}`}
          data-testid="fraud-filter-vpn"
        >
          <p className="text-xs text-slate-500 mb-1">VPN</p>
          <p className="text-lg font-bold text-amber-600">{fraudStats.vpn_users || 0}</p>
        </div>
        <div 
          onClick={() => handleClick("proxy")}
          className={`bg-white rounded-lg p-3 border cursor-pointer transition-all hover:scale-105 ${isActive("proxy") ? 'border-amber-500 ring-2 ring-amber-200 bg-amber-50' : 'border-slate-200 hover:border-amber-400'}`}
          data-testid="fraud-filter-proxy"
        >
          <p className="text-xs text-slate-500 mb-1">Proxy</p>
          <p className="text-lg font-bold text-amber-600">{fraudStats.proxy_users || 0}</p>
        </div>
        <div 
          onClick={() => handleClick("tor")}
          className={`bg-white rounded-lg p-3 border cursor-pointer transition-all hover:scale-105 ${isActive("tor") ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-slate-200 hover:border-red-400'}`}
          data-testid="fraud-filter-tor"
        >
          <p className="text-xs text-slate-500 mb-1">Tor</p>
          <p className="text-lg font-bold text-red-600">{fraudStats.tor_users || 0}</p>
        </div>
        {fraudStats.risk_breakdown && Object.entries(fraudStats.risk_breakdown).map(([level, count]) => (
          <div 
            key={level} 
            onClick={() => handleClick(level)}
            className={`bg-white rounded-lg p-3 border cursor-pointer transition-all hover:scale-105 ${
              isActive(level) 
                ? `ring-2 ${level === 'critical' ? 'border-red-500 ring-red-200 bg-red-50' : level === 'high' ? 'border-orange-500 ring-orange-200 bg-orange-50' : level === 'medium' ? 'border-yellow-500 ring-yellow-200 bg-yellow-50' : 'border-green-500 ring-green-200 bg-green-50'}`
                : `${level === 'critical' ? 'border-red-300 hover:border-red-500' : level === 'high' ? 'border-orange-300 hover:border-orange-500' : level === 'medium' ? 'border-yellow-300 hover:border-yellow-500' : 'border-green-300 hover:border-green-500'}`
            }`}
            data-testid={`fraud-filter-${level}`}
          >
            <p className="text-xs text-slate-500 mb-1 capitalize">{level}</p>
            <p className={`text-lg font-bold ${level === 'critical' ? 'text-red-600' : level === 'high' ? 'text-orange-600' : level === 'medium' ? 'text-yellow-600' : 'text-green-600'}`}>{count}</p>
          </div>
        ))}
      </div>
      {activeFilter && activeFilter !== "all" && (
        <div className="mt-3 flex items-center gap-2">
          <Badge variant="secondary" className="flex items-center gap-1">
            Filtering by: <span className="font-semibold capitalize">{activeFilter}</span>
            <button 
              onClick={() => onFilterChange("all")}
              className="ml-1 hover:text-red-600"
            >
              ×
            </button>
          </Badge>
        </div>
      )}
    </div>
  );
}
